import { X } from "lucide-react";
import { Alert, Button, Modal } from "@marekl/adminer/components";
import type { ShownNotice } from "../../lib/queries/notices";
import { NoticeText } from "./NoticeText";
import { useStoredIds } from "./useStoredIds";
import styles from "./Notices.module.css";

interface Props {
  notices: ShownNotice[];
  /**
   * „Rozumím" on a popup: the caller makes it permanent (the server for a
   * signed-in reader, this browser on the login page). The popup is hidden
   * here at once either way.
   */
  onAcknowledge: (id: ShownNotice["id"]) => void;
  /** Layout of the lišta stack: full-width in the app, card-width outside it. */
  layout: "app" | "public";
  /**
   * Whose session-long dismissals these are. Per account, because the tab
   * outlives a logout: on a club's shared computer the next person to log
   * in must not inherit what the last one closed.
   */
  dismissKey: string;
  /**
   * Whether a lišta carries its × . Off on the public pages: a notice there
   * is for somebody about to log in, and hiding it hides the reason a login
   * may be refused.
   */
  dismissible?: boolean;
  /**
   * Whether this view draws the lišty at all. Off inside the app, where
   * `NoticeStrip` draws them under each page's top bar and this view keeps
   * only the popup.
   */
  banners?: boolean;
}

/**
 * Renders oznámení the server has already chosen for this reader: every
 * lišta as an `Alert`, and the first unanswered popup as a `Modal`, one at
 * a time.
 *
 * Closing either one (× on a lišta, × or Escape on a popup) puts it away
 * for this browser session only, so it comes back after the next login.
 * Only „Rozumím" answers a popup for good.
 */
export function NoticesView({
  notices,
  onAcknowledge,
  layout,
  dismissKey,
  dismissible = true,
  banners: showBanners = true,
}: Props) {
  const dismissed = useStoredIds("session", dismissKey);

  const banners = showBanners
    ? notices.filter((n) => n.display === "banner" && !dismissed.ids.has(n.id))
    : [];
  const popup = notices.find((n) => n.display === "popup" && !dismissed.ids.has(n.id));

  return (
    <>
      {banners.length > 0 && (
        <div className={layout === "app" ? styles.appStack : styles.publicStack}>
          {banners.map((n) => (
            <Alert
              key={n.id}
              variant={n.severity}
              title={n.title}
              action={
                dismissible && (
                  <Button
                    iconOnly
                    weight="ghost"
                    aria-label="Skrýt oznámení"
                    title="Skrýt oznámení"
                    startIcon={<X size={16} aria-hidden="true" />}
                    onClick={() => dismissed.add(n.id)}
                  />
                )
              }
            >
              <NoticeText text={n.body} />
            </Alert>
          ))}
        </div>
      )}
      {popup && (
        <Modal
          open
          title={popup.title}
          onClose={() => dismissed.add(popup.id)}
          width={520}
          footer={
            <Button
              variant="primary"
              onClick={() => {
                dismissed.add(popup.id);
                onAcknowledge(popup.id);
              }}
            >
              Rozumím
            </Button>
          }
        >
          <Alert variant={popup.severity}>
            <NoticeText text={popup.body} />
          </Alert>
        </Modal>
      )}
    </>
  );
}
